import { admin, getConfig, readAccessToken } from '@repletoryreach/sdk-api';
import type { RemoteFileSource } from '@repletoryreach/sdk-ui/file-viewer';
import type { Translate } from '../../../../i18n/I18nProvider';
import { formatDate, formatMinor } from '../../../../lib/format';
import { statusLabel, validationLabel } from './InvoicesTable';

/**
 * An invoice PDF as the file viewer wants it.
 *
 * The endpoint is authenticated, so a plain link cannot open it: a browser
 * navigation carries no `Authorization` header. The viewer fetches it with the
 * session's token through the SDK's own base URL, which is also why there is
 * no shareable link (`linkUrl: null`): the only URL is one that needs the
 * token on the request. Download and Print live inside the viewer.
 */
export function invoiceViewerSource(r: admin.AdminInvoice, t: Translate, tenantName?: string): RemoteFileSource {
  const cfg = getConfig();
  const token = readAccessToken();
  return {
    kind: 'remote',
    id: r.id,
    url: `${cfg.apiBaseUrl}${admin.invoicePdfUrl(r.id)}`,
    name: `${r.invoice_number}.pdf`,
    mime: 'application/pdf',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: cfg.http.credentials,
    linkUrl: null,
    title: r.invoice_number,
    details: [
      ...(tenantName ? [{ label: t('common.tenant'), value: tenantName }] : []),
      { label: t('common.status'), value: statusLabel(t, r.status) },
      { label: t('billing.invoices.columns.period'), value: t('billing.invoices.period', { from: formatDate(r.period_start), to: formatDate(r.period_end) }) },
      { label: t('billing.invoices.columns.totalInclVat'), value: formatMinor(r.total_minor, r.currency) },
      ...(r.issued_at ? [{ label: t('billing.invoices.columns.issue'), value: formatDate(r.issued_at) }] : []),
      { label: t('billing.invoices.columns.validation'), value: validationLabel(t, r.validation_status) }
    ]
  };
}
